<template>
  <NuxtLink
    class="flex flex-col"
    :to="{
      name: detailRouteName,
      params: { slug: route.params.slug, id: product.objectID },
    }"
    @click="onProductClick"
  >
    <GridBackground
      :size="columns === 1 ? 'large' : 'small'"
      class="relative overflow-hidden"
      :class="
        columns === 1
          ? 'flex h-200 items-center justify-center p-16'
          : isIphoneGrid
            ? 'aspect-square flex items-start md:items-center justify-center'
            : 'aspect-square flex items-center justify-center p-16'
      "
    >
      <RevIllustration
        v-if="productImage"
        :image-class-names
        :alt="product.title"
        :height="imageHeight"
        :src="productImage"
        :width="imageWidth"
      />
    </GridBackground>

    <div class="mt-8 px-[10px] pb-16">
      <p class="body-1-bold line-clamp-2">{{ product.title_model }}</p>
      <p class="text-static-default-low body-2 line-clamp-2">
        {{
          [
            ...(product.sub_title_elements ?? []),
            i18n(translations.heavySignsOfUse),
          ].join(' • ')
        }}
      </p>

      <div v-if="outletPrice" class="mt-8 flex flex-col gap-4">
        <div>
          <OutletPriceTag :price="outletPrice" size="small" />
        </div>

        <FormattedMessage
          v-if="product.price_new_with_currency"
          class="caption text-static-default-mid"
          :definition="translations.priceNew"
        >
          <template #price>
            <span class="caption-striked">
              {{ product.price_new_with_currency }}
            </span>
          </template>
        </FormattedMessage>
      </div>
    </div>
  </NuxtLink>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed } from 'vue'

import type { AlgoliaProduct } from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useIsWebview } from '@backmarket/nuxt-module-navigation/useIsWebview'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevIllustration } from '@ds/components/Illustration'

import { NuxtLink } from '#components'

import { toWebviewRouteName } from '~/scopes/webviews/utils'

import GridBackground from '../../../../components/GridBackground/GridBackground.vue'
import OutletPriceTag from '../../../../components/OutletPriceTag/OutletPriceTag.vue'
import type { OutletDeviceType } from '../../../../composables/useProductImages/useProductImages'
import { OUTLET } from '../../../../routes-names'

import translations from './OutletProductCard.translations'

const props = withDefaults(
  defineProps<{
    columns?: 1 | 2
    deviceType?: OutletDeviceType | null
    images: string[]
    product: AlgoliaProduct
  }>(),
  { columns: 2, deviceType: null },
)

const i18n = useI18n()
const isWebview = useIsWebview()
const route = useRoute()
const { trackClick } = useTracking()

function onProductClick() {
  trackClick({
    zone: 'outlet_category',
    name: 'product_card',
    value: props.product.title_model,
  })
}

// Keep navigation within the current namespace: from a webview listing link to
// the webview PDP, otherwise to the standard PDP. Hardcoding the webview name
// left the standard `/outlet/...` listing pointing at a route that never
// matched, so taps did nothing.
const detailRouteName = computed(() =>
  isWebview ? toWebviewRouteName(OUTLET.DETAIL) : OUTLET.DETAIL,
)

const outletPrice = computed(() => ({
  amount: String(props.product.price),
  currency: props.product.currency,
}))

const productImage = computed(() => {
  return props.images[0] ?? ''
})

// iPhone cards (2-column grid) use a contained image with a top margin so
// the device does not touch the edge of the square cell. Other devices and
// the 1-column layout keep the original sizing behavior.
const isIphoneGrid = computed(
  () => props.columns !== 1 && props.deviceType === 'iphone',
)

const imageClassNames = computed(() => {
  if (isIphoneGrid.value) {
    return 'mt-8 h-[240px] w-[240px] object-contain'
  }

  if (props.columns === 1) {
    return 'h-200 w-auto max-w-full object-contain'
  }

  // Fallback images (not in the generated-images directory) use
  // object-contain so the Algolia image isn't cropped; generated macbook
  // images keep object-cover to fill the cell.
  return props.deviceType === null
    ? 'h-full w-full object-contain'
    : 'h-full w-full object-cover'
})

const imageHeight = computed(() => (isIphoneGrid.value ? 240 : 480))
const imageWidth = computed(() => (isIphoneGrid.value ? 240 : 480))
</script>
